import { useEffect, useRef, useState, type ReactNode } from 'react';
import { S, t, tList, type Leaf, type Locale } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { usePrefersReducedMotion } from '../../lib/motion';
import { useActions, useSteve } from '../../state/context';
import { MATERIALS, VIEWS, type MaterialName, type ViewName } from './scene-model';
import type { SceneHandle, SceneReadout } from './scene';
import styles from './threed.module.css';

/** Nombre de barres lues sur l'analyseur pour obtenir un niveau moyen. */
const LEVEL_BARS = 9;

/** Sous cette largeur : moins de pixels, ombre plus petite, pas de grille. */
const LOW_POWER_PX = 768;

/** Les quatre vues préréglées, dans l'ordre de la barre d'outils. */
const VIEW_LABELS: Record<ViewName, Leaf> = {
  threeQuarter: S.threed.tools.view_3q,
  front: S.threed.tools.view_front,
  side: S.threed.tools.view_side,
  top: S.threed.tools.view_top,
};

interface Props {
  /** Le relevé remonte au skin, qui l'affiche dans la barre du haut. */
  readonly onReadout: (readout: SceneReadout) => void;
}

/**
 * Le viewport 3D et sa barre d'outils.
 *
 * **three.js n'est chargé qu'ici, et à la demande.** Le reste du site tient dans
 * un seul paquet — c'est un choix assumé, le skin étant tiré au montage — mais
 * un moteur 3D de trois cents kilo-octets sur le chemin critique des huit autres
 * interfaces n'aurait aucun sens. L'import dynamique en fait un fragment à part,
 * téléchargé seulement si le tirage tombe ici.
 *
 * Le niveau audio vient de **l'analyseur déjà présent** dans le mixeur du site
 * (`readBars`), jamais d'un second : deux analyseurs sur le même graphe, c'est
 * du travail en double pour la même réponse.
 */
export function Steve3dViewport({ onReadout }: Props): ReactNode {
  const { lang, playing } = useSteve();
  const { readBars } = useActions();
  const reducedMotion = usePrefersReducedMotion();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gizmoRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<SceneHandle | null>(null);

  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const [material, setMaterial] = useState<MaterialName>('matte');
  const [view, setView] = useState<ViewName>('threeQuarter');
  const [spin, setSpin] = useState(!reducedMotion);
  const [wire, setWire] = useState(false);
  const [dance, setDance] = useState(!reducedMotion);

  // Les rappels changent à chaque rendu ; la scène, elle, ne se construit
  // qu'une fois. On passe donc par des refs.
  const readBarsRef = useRef(readBars);
  readBarsRef.current = readBars;
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const readoutRef = useRef(onReadout);
  readoutRef.current = onReadout;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    const levels = new Float32Array(LEVEL_BARS);

    void import('./scene')
      .then(({ createScene }) => {
        if (cancelled) return;
        const handle = createScene({
          canvas,
          readLevel: () => {
            if (!playingRef.current) return null;
            if (!readBarsRef.current(levels)) return null;
            let sum = 0;
            for (const value of levels) sum += value;
            return sum / levels.length;
          },
          onReadout: (readout) => {
            readoutRef.current(readout);
          },
          onOrientation: ({ azimuth, elevation }) => {
            // Écrit directement dans le style : à soixante images par seconde,
            // passer par l'état React ferait re-rendre tout le skin.
            const node = gizmoRef.current;
            if (node) {
              node.style.transform = `rotateX(${elevation.toFixed(1)}deg) rotateY(${(-azimuth).toFixed(1)}deg)`;
            }
          },
          reducedMotion,
          lowPower: window.innerWidth <= LOW_POWER_PX,
        });
        sceneRef.current = handle;
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    const onResize = (): void => {
      sceneRef.current?.resize();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
  }, [reducedMotion]);

  const tools: readonly { id: string; label: string; pressed?: boolean; onSelect: () => void }[] = [
    ...VIEWS.map((name) => ({
      id: name,
      label: t(VIEW_LABELS[name], lang),
      pressed: view === name,
      onSelect: () => {
        setView(name);
        sceneRef.current?.setView(name);
      },
    })),
    {
      id: 'spin',
      label: t(spin ? S.threed.tools.spin_on : S.threed.tools.spin_off, lang),
      pressed: spin,
      onSelect: () => {
        setSpin((on) => {
          sceneRef.current?.setSpin(!on);
          return !on;
        });
      },
    },
    {
      id: 'wire',
      label: t(wire ? S.threed.tools.wire_on : S.threed.tools.wire_off, lang),
      pressed: wire,
      onSelect: () => {
        setWire((on) => {
          sceneRef.current?.setWireframe(!on);
          return !on;
        });
      },
    },
    {
      id: 'dance',
      label: t(dance ? S.threed.tools.dance_on : S.threed.tools.dance_off, lang),
      pressed: dance,
      onSelect: () => {
        setDance((on) => {
          sceneRef.current?.setDance(!on);
          return !on;
        });
      },
    },
    {
      id: 'material',
      label: t(S.threed.tools.mat, lang, { name: materialLabel(material, lang) }),
      onSelect: () => {
        setMaterial((current) => {
          const next = MATERIALS[(MATERIALS.indexOf(current) + 1) % MATERIALS.length] ?? 'matte';
          sceneRef.current?.setMaterial(next);
          return next;
        });
      },
    },
  ];

  return (
    <div className={styles.stage}>
      <div className={styles.viewport}>
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          aria-label={t(S.threed.viewport.label, lang)}
          role="img"
        />

        <div className={styles.viewportHints} aria-hidden="true">
          <span>{t(S.threed.viewport.drag, lang)}</span>
          <span>{t(S.threed.viewport.wheel, lang)}</span>
        </div>

        {!ready && !failed && (
          <p className={styles.viewportState} role="status">
            {t(S.threed.viewport.loading, lang)}
          </p>
        )}
        {failed && (
          <p className={styles.viewportState} role="status">
            {t(S.threed.viewport.error, lang)}
          </p>
        )}

        {/* Le gizmo tourne avec la caméra réelle : c'est ce qui fait que
            l'habillage « connaît » la scène. */}
        <div className={styles.gizmoBox} aria-hidden="true">
          <div className={styles.gizmo} ref={gizmoRef}>
            <span className={cx(styles.axis, styles.axisX)} />
            <span className={cx(styles.axis, styles.axisY)} />
            <span className={cx(styles.axis, styles.axisZ)} />
          </div>
        </div>
      </div>

      <div className={styles.tools} role="toolbar" aria-label={t(S.threed.chrome.workspace, lang)}>
        {tools.map((tool) => (
          <button
            key={tool.id}
            type="button"
            className={cx(styles.tool, tool.pressed === true && styles.toolOn)}
            {...(tool.pressed === undefined ? {} : { 'aria-pressed': tool.pressed })}
            onClick={tool.onSelect}
          >
            {tool.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Nom de la matière courante.
 *
 * Les noms se traduisent, **l'ordre du cycle ne change jamais** : mat, satiné,
 * métallisé, miroir. La liste i18n suit donc `MATERIALS`, index par index.
 */
function materialLabel(name: MaterialName, lang: Locale): string {
  return tList(S.threed.tools.mat_names, lang)[MATERIALS.indexOf(name)] ?? name;
}
